import React, { useState } from 'react';

// Define types for our component
interface JsonToSchemaProps {}

// Schema format options
type SchemaFormat = 'jsonSchema' | 'openapi' | 'simple';

// Schema version options
type JsonSchemaVersion = 'draft-07' | 'draft-2019-09' | 'draft-2020-12';

// Initial display options
type DisplayOption = 'expanded' | 'collapsed';

// Sample data for the example
const sampleData = {
  "person": {
    "name": "John Doe",
    "age": 30,
    "email": "john.doe@example.com",
    "isActive": true,
    "address": {
      "street": "123 Main St",
      "city": "Anytown",
      "zipCode": "12345"
    },
    "tags": ["developer", "javascript", "typescript"],
    "projects": [
      {
        "id": 1,
        "name": "Website Redesign",
        "completed": true
      },
      {
        "id": 2,
        "name": "API Development",
        "completed": false
      }
    ]
  }
};

const JsonToSchema: React.FC<JsonToSchemaProps> = () => {
  // State for the JSON input
  const [jsonInput, setJsonInput] = useState<string>('');
  // State for the schema output
  const [schemaOutput, setSchemaOutput] = useState<string>('');
  // State for error messages
  const [error, setError] = useState<string>('');
  // State for the file name
  const [fileName, setFileName] = useState<string>('json_schema');
  // State for loading indicator
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // State for showing advanced options
  const [showAdvanced, setShowAdvanced] = useState<boolean>(false);
  // State for schema format
  const [schemaFormat, setSchemaFormat] = useState<SchemaFormat>('jsonSchema');
  // State for JSON Schema version
  const [schemaVersion, setSchemaVersion] = useState<JsonSchemaVersion>('draft-07');
  // State for includeExamples options
  const [includeExamples, setIncludeExamples] = useState<boolean>(true);
  // State for array detection options
  const [arrayDetection, setArrayDetection] = useState<string>('first');
  // State for display option
  const [displayOption, setDisplayOption] = useState<DisplayOption>('expanded');
  // State to track if the conversion happened
  const [isSchemaGenerated, setIsSchemaGenerated] = useState<boolean>(false);
  // State for copied indicator
  const [copied, setCopied] = useState<boolean>(false);
  // State for strict mode (required properties)
  const [strictMode, setStrictMode] = useState<boolean>(false);
  // State for schema title
  const [schemaTitle, setSchemaTitle] = useState<string>('Generated Schema');
  // State for schema description
  const [schemaDescription, setSchemaDescription] = useState<string>('Generated from JSON data');

  // Handle JSON input change
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setJsonInput(e.target.value);
    setError('');
    setIsSchemaGenerated(false);
  };

  // Handle file name change
  const handleFileNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFileName(e.target.value);
  };

  // Use example data
  const useExampleData = () => {
    setJsonInput(JSON.stringify(sampleData, null, 2));
    setError('');
    setIsSchemaGenerated(false);
    setSchemaOutput('');
  };

  // Beautify/Format JSON
  const beautifyJson = () => {
    try {
      if (!jsonInput.trim()) {
        setError('Please enter JSON data to format');
        return;
      }
      
      const parsedJson = JSON.parse(jsonInput);
      const beautified = JSON.stringify(parsedJson, null, 2);
      setJsonInput(beautified);
      setError('');
    } catch (e) {
      setError(`Invalid JSON format: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  // Validate JSON
  const validateJson = (jsonString: string): boolean => {
    try {
      JSON.parse(jsonString);
      return true;
    } catch (e) {
      return false;
    }
  };

  // Clear all inputs and reset states
  const clearAll = () => {
    setJsonInput('');
    setSchemaOutput('');
    setError('');
    setFileName('json_schema');
    setSchemaTitle('Generated Schema');
    setSchemaDescription('Generated from JSON data');
    setSchemaFormat('jsonSchema');
    setSchemaVersion('draft-07');
    setIncludeExamples(true);
    setArrayDetection('first');
    setStrictMode(false);
    setIsSchemaGenerated(false);
  };

  // Helper to infer type of a value
  const inferType = (value: any): string => {
    if (value === null) return 'null';
    if (Array.isArray(value)) return 'array';
    return typeof value;
  };

  // Helper to get JSON Schema type based on JavaScript type
  const getJsonSchemaType = (jsType: string): string | string[] => {
    switch (jsType) {
      case 'string': return 'string';
      case 'number': return 'number';
      case 'boolean': return 'boolean';
      case 'object': return 'object';
      case 'array': return 'array';
      case 'null': return 'null';
      // In some cases, we might not be able to determine the type
      // JSON Schema allows for multiple types
      default: return ['string', 'number', 'boolean', 'object', 'array', 'null'];
    }
  };

  // Helper to generate a schema for array items based on array contents
  const generateArrayItemsSchema = (arr: any[]): any => {
    if (arr.length === 0) {
      // Empty array, cannot determine items schema
      return {};
    }

    if (arrayDetection === 'first') {
      // Use the first item to determine the schema
      const firstItem = arr[0];
      const itemType = inferType(firstItem);
      
      if (itemType === 'object') {
        return {
          type: 'object',
          properties: generatePropertiesSchema(firstItem)
        };
      } else if (itemType === 'array') {
        return {
          type: 'array',
          items: generateArrayItemsSchema(firstItem)
        };
      } else {
        return {
          type: getJsonSchemaType(itemType)
        };
      }
    } else if (arrayDetection === 'all') {
      // Check if all items are of the same type
      const types = new Set(arr.map(item => inferType(item)));
      
      if (types.size === 1) {
        const itemType = Array.from(types)[0];
        
        if (itemType === 'object') {
          // Merge schemas of all objects in the array
          const mergedProperties: Record<string, any> = {};
          const requiredProperties = new Set<string>();
          
          // First, collect all possible properties
          arr.forEach(item => {
            Object.keys(item).forEach(key => {
              if (!(key in mergedProperties)) {
                mergedProperties[key] = {
                  type: getJsonSchemaType(inferType(item[key]))
                };
                
                // If we're in strict mode and the property exists in all objects, mark it as required
                if (strictMode) {
                  requiredProperties.add(key);
                }
              }
            });
          });
          
          // Then, check which properties are in all objects
          if (strictMode) {
            arr.forEach(item => {
              const itemKeys = new Set(Object.keys(item));
              Array.from(requiredProperties).forEach(key => {
                if (!itemKeys.has(key)) {
                  requiredProperties.delete(key);
                }
              });
            });
          }
          
          // Further refine each property's schema
          Object.keys(mergedProperties).forEach(key => {
            const values = arr.map(item => item[key]).filter(value => value !== undefined);
            
            if (values.length > 0) {
              const firstValue = values[0];
              const valueType = inferType(firstValue);
              
              if (valueType === 'object') {
                mergedProperties[key] = {
                  type: 'object',
                  properties: generatePropertiesSchema(firstValue)
                };
              } else if (valueType === 'array') {
                mergedProperties[key] = {
                  type: 'array',
                  items: generateArrayItemsSchema(firstValue)
                };
              }
              
              // Add example if requested
              if (includeExamples) {
                mergedProperties[key].examples = [firstValue];
              }
            }
          });
          
          const result: Record<string, any> = {
            type: 'object',
            properties: mergedProperties
          };
          
          if (strictMode && requiredProperties.size > 0) {
            result.required = Array.from(requiredProperties);
          }
          
          return result;
        } else if (itemType === 'array') {
          // Recursively determine schema for nested arrays
          return {
            type: 'array',
            items: generateArrayItemsSchema(arr[0])
          };
        } else {
          return {
            type: getJsonSchemaType(itemType)
          };
        }
      } else {
        // Items have different types, use oneOf
        const typeSchemas = Array.from(types).map(type => {
          return { type: getJsonSchemaType(type) };
        });
        
        return {
          oneOf: typeSchemas
        };
      }
    } else {
      // Mixed approach - try to find the most specific common type
      const types = new Set(arr.map(item => inferType(item)));
      
      if (types.size === 1) {
        // All items are the same type
        return generateArrayItemsSchema(arr); // Call the same function with 'all' approach
      } else {
        // Mixed types, use anyOf or a more generic schema
        return {}; // Allow any type
      }
    }
  };

  // Helper to generate properties schema for objects
  const generatePropertiesSchema = (obj: Record<string, any>): Record<string, any> => {
    const properties: Record<string, any> = {};
    const required: string[] = [];
    
    Object.entries(obj).forEach(([key, value]) => {
      const valueType = inferType(value);
      const property: Record<string, any> = {
        type: getJsonSchemaType(valueType)
      };
      
      // Add example if requested
      if (includeExamples) {
        property.examples = [value];
      }
      
      // Add more specific schema for objects and arrays
      if (valueType === 'object' && value !== null) {
        property.properties = generatePropertiesSchema(value);
        
        // If in strict mode, add required properties
        if (strictMode && Object.keys(value).length > 0) {
          property.required = Object.keys(value);
        }
      } else if (valueType === 'array') {
        property.items = generateArrayItemsSchema(value);
      }
      
      properties[key] = property;
      
      // In strict mode, all properties are required
      if (strictMode) {
        required.push(key);
      }
    });
    
    return properties;
  };

  // Generate JSON Schema
  const generateJsonSchema = (data: any): any => {
    const rootType = inferType(data);
    let schema: Record<string, any> = {
      $schema: getSchemaUrl(),
      title: schemaTitle,
      description: schemaDescription
    };
    
    if (rootType === 'object') {
      schema.type = 'object';
      schema.properties = generatePropertiesSchema(data);
      
      // If in strict mode, add required properties
      if (strictMode && Object.keys(data).length > 0) {
        schema.required = Object.keys(data);
      }
    } else if (rootType === 'array') {
      schema.type = 'array';
      schema.items = generateArrayItemsSchema(data);
    } else {
      schema.type = getJsonSchemaType(rootType);
    }
    
    return schema;
  };

  // Generate Simple Schema
  const generateSimpleSchema = (data: any): any => {
    const rootType = inferType(data);
    let schema: Record<string, any> = {
      title: schemaTitle,
      description: schemaDescription
    };
    
    const simplifyType = (type: string | string[]): string => {
      if (Array.isArray(type)) {
        return type.join('|');
      }
      return type;
    };
    
    const simplifyProperties = (obj: Record<string, any>): Record<string, any> => {
      const properties: Record<string, any> = {};
      
      Object.entries(obj).forEach(([key, value]) => {
        const valueType = inferType(value);
        
        if (valueType === 'object' && value !== null) {
          properties[key] = {
            type: 'object',
            properties: simplifyProperties(value)
          };
        } else if (valueType === 'array') {
          const itemSchema = value.length > 0 ? 
            { type: simplifyType(getJsonSchemaType(inferType(value[0]))) } : 
            { type: 'any' };
          
          properties[key] = {
            type: 'array',
            items: itemSchema
          };
        } else {
          properties[key] = {
            type: simplifyType(getJsonSchemaType(valueType))
          };
        }
      });
      
      return properties;
    };
    
    if (rootType === 'object') {
      schema.type = 'object';
      schema.properties = simplifyProperties(data);
    } else if (rootType === 'array') {
      schema.type = 'array';
      schema.items = data.length > 0 ? 
        { type: simplifyType(getJsonSchemaType(inferType(data[0]))) } : 
        { type: 'any' };
    } else {
      schema.type = simplifyType(getJsonSchemaType(rootType));
    }
    
    return schema;
  };

  // Generate OpenAPI Schema
  const generateOpenApiSchema = (data: any): any => {
    const rootType = inferType(data);
    let schema: Record<string, any> = {};
    
    const convertToOpenApiSchema = (obj: Record<string, any>): Record<string, any> => {
      const properties: Record<string, any> = {};
      const required: string[] = [];
      
      Object.entries(obj).forEach(([key, value]) => {
        const valueType = inferType(value);
        const property: Record<string, any> = {};
        
        if (valueType === 'string') {
          property.type = 'string';
          
          // Try to infer format from string content
          if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
            property.format = 'date';
          } else if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value)) {
            property.format = 'date-time';
          } else if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value)) {
            property.format = 'uuid';
          } else if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
            property.format = 'email';
          }
        } else if (valueType === 'number') {
          property.type = Number.isInteger(value) ? 'integer' : 'number';
        } else if (valueType === 'boolean') {
          property.type = 'boolean';
        } else if (valueType === 'object' && value !== null) {
          property.type = 'object';
          const nestedSchema = convertToOpenApiSchema(value);
          property.properties = nestedSchema.properties;
          
          if (nestedSchema.required && nestedSchema.required.length > 0) {
            property.required = nestedSchema.required;
          }
        } else if (valueType === 'array') {
          property.type = 'array';
          
          if (value.length > 0) {
            const firstItem = value[0];
            const itemType = inferType(firstItem);
            
            if (itemType === 'object') {
              const itemSchema = convertToOpenApiSchema(firstItem);
              property.items = {
                type: 'object',
                properties: itemSchema.properties
              };
              
              if (itemSchema.required && itemSchema.required.length > 0) {
                property.items.required = itemSchema.required;
              }
            } else {
              property.items = {
                type: itemType === 'number' && Number.isInteger(firstItem) ? 'integer' : itemType
              };
            }
          } else {
            property.items = {};
          }
        } else if (valueType === 'null') {
          property.nullable = true;
          property.type = 'string'; // Default type for nullable
        }
        
        // Add example if requested
        if (includeExamples && value !== null) {
          property.example = value;
        }
        
        properties[key] = property;
        
        // In strict mode, all properties are required
        if (strictMode) {
          required.push(key);
        }
      });
      
      const result: Record<string, any> = {
        properties: properties
      };
      
      if (strictMode && required.length > 0) {
        result.required = required;
      }
      
      return result;
    };
    
    schema.title = schemaTitle;
    schema.description = schemaDescription;
    
    if (rootType === 'object') {
      const converted = convertToOpenApiSchema(data);
      schema.type = 'object';
      schema.properties = converted.properties;
      
      if (converted.required && converted.required.length > 0) {
        schema.required = converted.required;
      }
    } else if (rootType === 'array') {
      schema.type = 'array';
      
      if (data.length > 0) {
        const firstItem = data[0];
        const itemType = inferType(firstItem);
        
        if (itemType === 'object') {
          const itemSchema = convertToOpenApiSchema(firstItem);
          schema.items = {
            type: 'object',
            properties: itemSchema.properties
          };
          
          if (itemSchema.required && itemSchema.required.length > 0) {
            schema.items.required = itemSchema.required;
          }
        } else {
          schema.items = {
            type: itemType === 'number' && Number.isInteger(firstItem) ? 'integer' : itemType
          };
        }
      } else {
        schema.items = {};
      }
    } else if (rootType === 'number') {
      schema.type = Number.isInteger(data) ? 'integer' : 'number';
      if (includeExamples) {
        schema.example = data;
      }
    } else {
      schema.type = rootType;
      if (includeExamples && data !== null) {
        schema.example = data;
      }
    }
    
    return schema;
  };

  // Get JSON Schema URL based on selected version
  const getSchemaUrl = (): string => {
    switch (schemaVersion) {
      case 'draft-07':
        return 'http://json-schema.org/draft-07/schema#';
      case 'draft-2019-09':
        return 'https://json-schema.org/draft/2019-09/schema';
      case 'draft-2020-12':
        return 'https://json-schema.org/draft/2020-12/schema';
      default:
        return 'http://json-schema.org/draft-07/schema#';
    }
  };

  // Convert JSON to Schema
  const convertJsonToSchema = () => {
    setError('');
    setIsLoading(true);
    setIsSchemaGenerated(false);
    setSchemaOutput('');
    
    if (!jsonInput.trim()) {
      setError('Please enter JSON data');
      setIsLoading(false);
      return;
    }

    if (!validateJson(jsonInput)) {
      setError('Invalid JSON format. Please check your input.');
      setIsLoading(false);
      return;
    }

    try {
      const jsonData = JSON.parse(jsonInput);
      let schema;
      
      switch (schemaFormat) {
        case 'jsonSchema':
          schema = generateJsonSchema(jsonData);
          break;
        case 'openapi':
          schema = generateOpenApiSchema(jsonData);
          break;
        case 'simple':
          schema = generateSimpleSchema(jsonData);
          break;
        default:
          schema = generateJsonSchema(jsonData);
      }
      
      setSchemaOutput(JSON.stringify(schema, null, 2));
      setIsSchemaGenerated(true);
      setIsLoading(false);
    } catch (e) {
      setError(`Error generating schema: ${e instanceof Error ? e.message : String(e)}`);
      setIsLoading(false);
    }
  };

  // Copy schema to clipboard
  const copyToClipboard = () => {
    navigator.clipboard.writeText(schemaOutput)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {
        setError('Failed to copy to clipboard');
      });
  };

  // Download schema file
  const downloadSchema = () => {
    const blob = new Blob([schemaOutput], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    
    link.href = url;
    link.download = `${fileName || 'json_schema'}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">JSON to Schema Converter</h1>
      
      {/* Description */}
      <div className="mb-6 bg-blue-50 p-4 rounded-md border border-blue-100">
        <p className="text-blue-800">
          Convert JSON data to JSON Schema. Paste your JSON data below to automatically generate a schema that describes its structure.
        </p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Input Section */}
        <div className="flex flex-col h-full">
          <div className="bg-white p-4 rounded-md shadow-sm mb-4 flex-grow">
            <div className="flex justify-between items-center mb-3">
              <label htmlFor="jsonInput" className="font-medium text-gray-700">
                JSON Input
              </label>
              <div className="flex space-x-2">
                <button
                  className="text-sm text-blue-600 hover:text-blue-800"
                  onClick={useExampleData}
                >
                  Use Example
                </button>
                <button
                  className="text-sm text-blue-600 hover:text-blue-800"
                  onClick={beautifyJson}
                >
                  Format JSON
                </button>
                <button
                  className="text-sm text-gray-600 hover:text-gray-800"
                  onClick={() => setJsonInput('')}
                >
                  Clear
                </button>
              </div>
            </div>
            <textarea
              id="jsonInput"
              className="w-full h-80 p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
              value={jsonInput}
              onChange={handleInputChange}
              placeholder='Paste your JSON data here, e.g., {"name": "John", "age": 30}'
            />
          </div>
          
          {/* Configuration */}
          <div className="bg-white p-4 rounded-md shadow-sm">
            <div className="mb-4">
              <label htmlFor="fileName" className="block text-sm font-medium text-gray-700 mb-1">
                File Name
              </label>
              <input
                type="text"
                id="fileName"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                value={fileName}
                onChange={handleFileNameChange}
                placeholder="Enter file name (without extension)"
              />
            </div>

            <div className="mb-4">
              <label htmlFor="schemaTitle" className="block text-sm font-medium text-gray-700 mb-1">
                Schema Title
              </label>
              <input
                type="text"
                id="schemaTitle"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                value={schemaTitle}
                onChange={(e) => setSchemaTitle(e.target.value)}
                placeholder="Enter schema title"
              />
            </div>

            <div className="mb-4">
              <label htmlFor="schemaDescription" className="block text-sm font-medium text-gray-700 mb-1">
                Schema Description
              </label>
              <input
                type="text"
                id="schemaDescription"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                value={schemaDescription}
                onChange={(e) => setSchemaDescription(e.target.value)}
                placeholder="Enter schema description"
              />
            </div>
            
            <div className="mb-4">
              <button
                className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                onClick={() => setShowAdvanced(!showAdvanced)}
              >
                <span>{showAdvanced ? 'Hide' : 'Show'} Advanced Options</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className={`h-4 w-4 ml-1 transition-transform ${showAdvanced ? 'transform rotate-180' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>
            
            {showAdvanced && (
              <div className="space-y-4 p-3 bg-gray-50 rounded-md mb-4">
                <div>
                  <label htmlFor="includeExamples" className="ml-2 block text-sm text-gray-700">
                    Include Examples in Schema
                  </label>
                </div>
              </div>
            )}
            
            <div className="mt-4 flex justify-between">
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                onClick={convertJsonToSchema}
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </div>
                ) : (
                  'Generate Schema'
                )}
              </button>
              
              <button
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                onClick={clearAll}
              >
                Clear All
              </button>
            </div>
          </div>
        </div>
        
        {/* Output Section */}
        <div className="bg-white p-4 rounded-md shadow-sm h-full flex flex-col">
          <div className="flex justify-between items-center mb-3">
            <label className="font-medium text-gray-700">
              Schema Output
            </label>
            
            {isSchemaGenerated && (
              <div className="flex space-x-2">
                <button
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                  onClick={copyToClipboard}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  {copied ? 'Copied!' : 'Copy Schema'}
                </button>
                
                <button
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                  onClick={downloadSchema}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download
                </button>
              </div>
            )}
          </div>
          
          <div className="flex-grow flex flex-col">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-md mb-3">
                <p className="font-medium">Error</p>
                <p>{error}</p>
              </div>
            )}
            
            {!error && !isSchemaGenerated && (
              <div className="bg-gray-50 border border-gray-200 text-gray-500 p-4 rounded-md flex-grow flex items-center justify-center">
                <div className="text-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                  <p>
                    Schema output will appear here after generation.
                  </p>
                </div>
              </div>
            )}
            
            {!error && isSchemaGenerated && (
              <div className="flex-grow flex flex-col">
                <div className="flex-grow flex flex-col">
                  <textarea
                    id="schemaOutput"
                    className="w-full flex-grow p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
                    value={schemaOutput}
                    readOnly
                  />
                </div>
              </div>
            )}
          </div>
          
          {isSchemaGenerated && (
            <div className="mt-4 bg-green-50 border border-green-200 text-green-700 p-3 rounded-md">
              <div className="flex items-center mb-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <p className="font-medium">Schema Successfully Generated!</p>
              </div>
              <p className="text-sm">
                The generated schema describes the structure of your JSON data and can be used for validation, documentation, or API specifications.
              </p>
            </div>
          )}
        </div>
      </div>
      
      {/* Information Section */}
      <div className="mt-8 bg-white p-6 rounded-md shadow-sm">
        <h2 className="text-lg font-semibold mb-4">About JSON to Schema Conversion</h2>
        
        <div className="space-y-4">
          <div>
            <h3 className="font-medium text-gray-800 mb-2">How This Converter Works</h3>
            <p className="text-gray-600">
              This tool analyzes your JSON data and automatically generates a schema that describes its structure.
              The converter infers types, identifies required properties, and creates a hierarchical representation of your data.
            </p>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Features</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li><strong>Multiple Schema Formats</strong> - Generate JSON Schema, OpenAPI Schema, or Simple Schema</li>
              <li><strong>Type Inference</strong> - Automatically detect data types for all properties</li>
              <li><strong>Nested Structure Support</strong> - Handle complex, nested JSON objects and arrays</li>
              <li><strong>Examples Inclusion</strong> - Optionally include example values from your JSON data</li>
              <li><strong>Required Properties</strong> - Use strict mode to define required properties</li>
              <li><strong>Custom Metadata</strong> - Set title and description for your schema</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Uses for JSON Schema</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li><strong>API Documentation</strong> - Describe request and response formats</li>
              <li><strong>Data Validation</strong> - Validate JSON data against a schema</li>
              <li><strong>Form Generation</strong> - Generate forms from schema definitions</li>
              <li><strong>Code Generation</strong> - Create models or types from schema definitions</li>
              <li><strong>Database Schemas</strong> - Define document structures for NoSQL databases</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Tips for Better Results</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li>Use representative JSON data that includes all possible fields</li>
              <li>Format your JSON before conversion to ensure it's valid</li>
              <li>For arrays, choose "Analyze All Items" option for more accurate schemas</li>
              <li>Enable "Strict Mode" to identify required properties</li>
              <li>Use "OpenAPI Schema" format if you're working with Swagger or OpenAPI documentation</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JsonToSchema; 